import { Component } from '@theme/component';
import { debounce, onAnimationEnd, prefersReducedMotion, onDocumentLoaded } from '@theme/utilities';
import { sectionRenderer } from '@theme/section-renderer';
import { morph } from '@theme/morph';
import { RecentlyViewed } from '@theme/recently-viewed-products';
import { DialogCloseEvent, DialogComponent } from '@theme/dialog';

/** Filter parameter that restricts the search to the products tagged as visible. */
const VISIBILITY_TAG_PARAM = 'filter.p.tag';

/**
 * A custom element that allows the user to search for resources available on the store.
 *
 * @typedef {object} Refs
 * @property {HTMLInputElement} searchInput - The search input element.
 * @property {HTMLElement} predictiveSearchResults - The predictive search results container.
 * @property {HTMLElement} resetButton - The reset button element.
 * @property {HTMLElement[]} [resultsItems] - The search results items elements.
 * @property {HTMLElement} [recentlyViewedWrapper] - The recently viewed products wrapper.
 * @property {HTMLElement[]} [recentlyViewedTitle] - The recently viewed title elements.
 * @property {HTMLElement[]} [recentlyViewedItems] - The recently viewed product items.
 * @extends {Component<Refs>}
 */
class PredictiveSearchComponent extends Component {
  requiredRefs = ['searchInput', 'predictiveSearchResults', 'resetButton'];

  #controller = new AbortController();

  /**
   * @type {AbortController | null}
   */
  #activeFetch = null;

  /**
   * Get the dialog component.
   * @returns {DialogComponent | null} The dialog component.
   */
  get dialog() {
    return this.closest('dialog-component');
  }

  connectedCallback() {
    super.connectedCallback();

    const { dialog } = this;
    const { signal } = this.#controller;

    if (this.refs.searchInput.value.length > 0) {
      this.#showResetButton();
    }

    if (dialog) {
      document.addEventListener('keydown', this.#handleKeyboardShortcut, { signal });
      dialog.addEventListener(DialogCloseEvent.eventName, this.#handleDialogClose, { signal });

      this.addEventListener('click', this.#handleModalClick, { signal });
    }

    onDocumentLoaded(() => {
      this.resetSearch(false); // Pass false to avoid focusing the input
    });
  }

  /**
   * Handles clicks within the predictive search modal to maintain focus on the input
   * @param {MouseEvent} event - The mouse event
   */
  #handleModalClick = (event) => {
    const target = /** @type {HTMLElement} */ (event.target);
    const isInteractiveElement =
      target instanceof HTMLButtonElement ||
      target instanceof HTMLAnchorElement ||
      target instanceof HTMLInputElement ||
      target.closest('button') ||
      target.closest('a') ||
      target.closest('input');

    if (!isInteractiveElement && this.refs.searchInput) {
      this.refs.searchInput.focus();
    }
  };

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#controller.abort();
  }

  /**
   * Handles the CMD+K key combination.
   * @param {KeyboardEvent} event - The keyboard event.
   */
  #handleKeyboardShortcut = (event) => {
    if (event.metaKey && event.key === 'k') {
      this.dialog?.toggleDialog();
    }
  };

  /**
   * Handles the dialog close event.
   */
  #handleDialogClose = () => {
    this.#resetSearch();
  };

  get #allResultsItems() {
    const containers = Array.from(
      this.querySelectorAll(
        '.predictive-search-results__wrapper-queries, ' +
          '.predictive-search-results__wrapper-products, ' +
          '.predictive-search-results__list'
      )
    );

    const allItems = containers
      .flatMap((container) => {
        if (container.classList.contains('predictive-search-results__wrapper-products')) {
          return Array.from(container.querySelectorAll('.predictive-search-results__card'));
        }
        return Array.from(container.querySelectorAll('[ref="resultsItems[]"], .predictive-search-results__card'));
      })
      .filter((item) => item instanceof HTMLElement);

    return /** @type {HTMLElement[]} */ (allItems);
  }

  /**
   * Track whether the last interaction was keyboard-based
   * @type {boolean}
   */
  #isKeyboardNavigation = false;

  get #currentIndex() {
    return this.#allResultsItems?.findIndex((item) => item.getAttribute('aria-selected') === 'true') ?? -1;
  }

  set #currentIndex(index) {
    if (!this.#allResultsItems?.length) return;

    this.#allResultsItems.forEach((item) => {
      item.classList.remove('keyboard-focus');
    });

    for (const [itemIndex, item] of this.#allResultsItems.entries()) {
      if (itemIndex === index) {
        item.setAttribute('aria-selected', 'true');

        if (this.#isKeyboardNavigation) {
          item.classList.add('keyboard-focus');
        }
        item.scrollIntoView({ behavior: prefersReducedMotion() ? 'instant' : 'smooth', block: 'nearest' });
      } else {
        item.removeAttribute('aria-selected');
      }
    }
    this.refs.searchInput.focus();
  }

  get #currentItem() {
    return this.#allResultsItems?.[this.#currentIndex];
  }

  /**
   * Navigate through the predictive search results using arrow keys or close them with the Escape key.
   * @param {KeyboardEvent} event - The keyboard event.
   */
  onSearchKeyDown = (event) => {
    if (event.key === 'Escape') {
      this.#resetSearch();
      return;
    }

    if (!this.#allResultsItems?.length || event.key === 'ArrowLeft' || event.key === 'ArrowRight') {
      return;
    }

    const currentIndex = this.#currentIndex;
    const totalItems = this.#allResultsItems.length;

    switch (event.key) {
      case 'ArrowDown':
        this.#isKeyboardNavigation = true;
        event.preventDefault();
        this.#currentIndex = currentIndex < totalItems - 1 ? currentIndex + 1 : 0;
        break;

      case 'Tab':
        if (event.shiftKey) {
          this.#isKeyboardNavigation = true;
          event.preventDefault();
          this.#currentIndex = currentIndex > 0 ? currentIndex - 1 : totalItems - 1;
        } else {
          this.#isKeyboardNavigation = true;
          event.preventDefault();
          this.#currentIndex = currentIndex < totalItems - 1 ? currentIndex + 1 : 0;
        }
        break;

      case 'ArrowUp':
        this.#isKeyboardNavigation = true;
        event.preventDefault();
        this.#currentIndex = currentIndex > 0 ? currentIndex - 1 : totalItems - 1;
        break;

      case 'Enter': {
        const singleResultContainer = this.refs.predictiveSearchResults.querySelector('[data-single-result-url]');
        if (singleResultContainer instanceof HTMLElement && singleResultContainer.dataset.singleResultUrl) {
          event.preventDefault();
          window.location.href = singleResultContainer.dataset.singleResultUrl;
          return;
        }

        if (this.#currentIndex >= 0) {
          event.preventDefault();
          this.#currentItem?.querySelector('a')?.click();
        } else {
          const searchUrl = new URL(Theme.routes.search_url, location.origin);
          searchUrl.searchParams.set('q', this.refs.searchInput.value);
          if (this.#visibilityTag) searchUrl.searchParams.set(VISIBILITY_TAG_PARAM, this.#visibilityTag);
          window.location.href = searchUrl.toString();
        }
        break;
      }
    }
  };

  /**
   * Clears the recently viewed products.
   * @param {Event} event - The event.
   */
  clearRecentlyViewedProducts(event) {
    event.stopPropagation();

    RecentlyViewed.clearProducts();

    const { recentlyViewedItems, recentlyViewedTitle, recentlyViewedWrapper } = this.refs;

    const allRecentlyViewedElements = [...(recentlyViewedItems || []), ...(recentlyViewedTitle || [])];

    if (allRecentlyViewedElements.length === 0) {
      return;
    }

    if (recentlyViewedWrapper) {
      recentlyViewedWrapper.classList.add('removing');

      onAnimationEnd(recentlyViewedWrapper, () => {
        recentlyViewedWrapper.remove();
      });
    }
  }

  /**
   * Reset the search state.
   * @param {boolean} [keepFocus=true] - Whether to keep focus on input after reset
   */
  resetSearch = debounce((keepFocus = true) => {
    if (keepFocus) {
      this.refs.searchInput.focus();
    }
    this.#resetSearch();
  }, 100);

  /**
   * Debounce the search handler to fetch and display search results based on the input value.
   * Reset the current selection index and close results if the search term is empty.
   */
  search = debounce((event) => {
    // If the input is not a text input (like using the Escape key), don't search
    if (!event.inputType) return;

    const searchTerm = this.refs.searchInput.value.trim();
    this.#currentIndex = -1;

    if (!searchTerm.length) {
      this.#resetSearch();
      return;
    }

    this.#showResetButton();
    this.#getSearchResults(searchTerm);
  }, 200);

  /**
   * Resets scroll positions for search results containers
   */
  #resetScrollPositions() {
    requestAnimationFrame(() => {
      const resultsInner = this.refs.predictiveSearchResults.querySelector('.predictive-search-results__inner');
      if (resultsInner instanceof HTMLElement) {
        resultsInner.scrollTop = 0;
      }

      const formContent = this.querySelector('.predictive-search-form__content');
      if (formContent instanceof HTMLElement) {
        formContent.scrollTop = 0;
      }
    });
  }

  /**
   * Fetch search results using the section renderer and update the results container.
   * @param {string} searchTerm - The term to search for
   */
  async #getSearchResults(searchTerm) {
    if (!this.dataset.sectionId) return;

    const url = new URL(Theme.routes.predictive_search_url, location.origin);
    url.searchParams.set('q', searchTerm);
    url.searchParams.set('resources[limit_scope]', 'each');
    // Products come from the regular search below, the predictive API can't filter by tag
    url.searchParams.set('resources[type]', 'query,collection,page');

    const productsUrl = new URL(Theme.routes.search_url, location.origin);
    productsUrl.searchParams.set('q', searchTerm);
    productsUrl.searchParams.set('type', 'product');
    productsUrl.searchParams.set('options[prefix]', 'last');
    if (this.#visibilityTag) productsUrl.searchParams.set(VISIBILITY_TAG_PARAM, this.#visibilityTag);

    const { predictiveSearchResults } = this.refs;

    const abortController = this.#createAbortController();
    const sectionId = this.dataset.sectionId;

    // Muaré: además de los productos de la colección actual (etiqueta WEB) se pide la búsqueda
    // sin filtro. Sirve de respaldo si no hay ningún producto WEB y, si Shopify coloca primero
    // un producto sin WEB (han escrito su nombre), se añade una fila "Colecciones anteriores".
    const allProductsUrl = new URL(productsUrl);
    allProductsUrl.searchParams.delete(VISIBILITY_TAG_PARAM);

    try {
      let [resultsMarkup, productsMarkup, allProductsMarkup] = await Promise.all([
        sectionRenderer.getSectionHTML(sectionId, false, url),
        sectionRenderer.getSectionHTML(sectionId, false, productsUrl),
        this.#visibilityTag ? sectionRenderer.getSectionHTML(sectionId, false, allProductsUrl) : Promise.resolve(null),
      ]);

      if (abortController.signal.aborted) return;

      let olderProducts = null;
      if (this.#visibilityTag && allProductsMarkup) {
        if (this.#countProducts(productsMarkup) === 0) {
          productsMarkup = allProductsMarkup;
        } else {
          olderProducts = this.#olderProducts(allProductsMarkup);
        }
      }

      if (!resultsMarkup) return;

      morph(predictiveSearchResults, this.#mergeProducts(resultsMarkup, productsMarkup, olderProducts));

      this.#resetScrollPositions();
    } catch (error) {
      if (abortController.signal.aborted) return;
      throw error;
    }
  }

  /**
   * The tag a product needs to show up in search, read from the form's hidden filter input.
   * @returns {string} The tag, or an empty string if the form doesn't restrict the search.
   */
  get #visibilityTag() {
    const input = this.querySelector(`input[name="${VISIBILITY_TAG_PARAM}"]`);
    return input instanceof HTMLInputElement ? input.value : '';
  }

  /**
   * Counts the products rendered by the product search section markup.
   * @param {string | null | undefined} productsMarkup - The section markup rendered from the product search.
   * @returns {number} The number of products.
   */
  #countProducts(productsMarkup) {
    const productsDocument = new DOMParser().parseFromString(productsMarkup ?? '', 'text/html');
    const meta = productsDocument.querySelector('[data-predictive-products-meta]');
    return meta instanceof HTMLElement ? Number(meta.dataset.count) : 0;
  }

  /**
   * Builds the "previous collections" block from the unfiltered product search.
   * Only when Shopify ranks a product without the visibility tag first (the customer typed its name).
   * @param {string} allProductsMarkup - The section markup rendered from the unfiltered product search.
   * @returns {HTMLElement | null} The block, or null when it shouldn't be shown.
   */
  #olderProducts(allProductsMarkup) {
    const doc = new DOMParser().parseFromString(allProductsMarkup ?? '', 'text/html');
    const meta = doc.querySelector('[data-predictive-products-meta]');
    const container = doc.getElementById('predictive-search-products');
    if (!(meta instanceof HTMLElement) || !container || meta.dataset.firstWeb !== 'false') return null;

    const items = Array.from(container.querySelectorAll('li[data-web="false"]')).slice(0, 4);
    if (items.length === 0) return null;

    container.querySelectorAll('li').forEach((li) => li.remove());
    const list = container.querySelector('ul');
    if (!list) return null;
    items.forEach((li) => list.append(li));

    const title = container.querySelector('.predictive-search-results__title');
    if (title && meta.dataset.olderTitle) title.textContent = meta.dataset.olderTitle;
    container.id = 'predictive-search-products-older';
    container.classList.add('predictive-search-results__products--older');
    return container;
  }

  /**
   * Inserts the products of the product search into the predictive results markup.
   * @param {string} resultsMarkup - The predictive search section markup (queries, collections, pages).
   * @param {string} productsMarkup - The section markup rendered from the product search.
   * @param {HTMLElement | null} [olderProducts] - Optional "previous collections" block to show after the products.
   * @returns {string} The merged markup.
   */
  #mergeProducts(resultsMarkup, productsMarkup, olderProducts = null) {
    const parser = new DOMParser();
    const results = parser.parseFromString(resultsMarkup, 'text/html');
    const slot = results.querySelector('[data-predictive-products-slot]');
    if (!(slot instanceof HTMLElement)) return resultsMarkup;

    const productsDocument = parser.parseFromString(productsMarkup ?? '', 'text/html');
    const products = productsDocument.getElementById('predictive-search-products');
    const meta = productsDocument.querySelector('[data-predictive-products-meta]');
    const productCount = products && meta instanceof HTMLElement ? Number(meta.dataset.count) : 0;
    const otherCount = Number(slot.dataset.otherCount);

    if (!products || !(meta instanceof HTMLElement) || productCount === 0) {
      slot.remove();
      return results.body.innerHTML;
    }

    slot.replaceWith(products);
    if (olderProducts) products.after(results.adoptNode(olderProducts));

    const noResults = results.querySelector('[data-predictive-no-results]');
    if (noResults) {
      noResults.remove();
      const status = results.querySelector('[data-predictive-status]');
      if (status) status.textContent = '';
    }

    results.querySelector('[data-single-result-url]')?.remove();
    if (productCount === 1 && otherCount === 0 && !olderProducts && meta.dataset.firstUrl) {
      const singleResult = results.createElement('div');
      singleResult.dataset.singleResultUrl = meta.dataset.firstUrl;
      results.querySelector('.predictive-search-results__inner')?.append(singleResult);
    }

    return results.body.innerHTML;
  }

  /**
   * Fetch the markup for the recently viewed products.
   * @returns {Promise<string | null>} The markup for the recently viewed products.
   */
  async #getRecentlyViewedProductsMarkup() {
    if (!this.dataset.sectionId) return null;

    const viewedProducts = RecentlyViewed.getProducts();
    if (viewedProducts.length === 0) return null;

    const url = new URL(Theme.routes.search_url, location.origin);
    url.searchParams.set('q', viewedProducts.map(/** @param {string} id */ (id) => `id:${id}`).join(' OR '));
    url.searchParams.set('resources[type]', 'product');

    return sectionRenderer.getSectionHTML(this.dataset.sectionId, false, url);
  }

  #hideResetButton() {
    const { resetButton } = this.refs;

    resetButton.hidden = true;
  }

  #showResetButton() {
    const { resetButton } = this.refs;

    resetButton.hidden = false;
  }

  #createAbortController() {
    const abortController = new AbortController();
    if (this.#activeFetch) {
      this.#activeFetch.abort();
    }
    this.#activeFetch = abortController;
    return abortController;
  }

  #resetSearch = async () => {
    const { predictiveSearchResults, searchInput } = this.refs;
    const emptySectionId = 'predictive-search-empty';

    this.#currentIndex = -1;
    searchInput.value = '';
    this.#hideResetButton();

    const abortController = this.#createAbortController();
    const url = new URL(window.location.href);
    url.searchParams.delete('page');

    const emptySectionMarkup = await sectionRenderer.getSectionHTML(emptySectionId, false, url);
    const parsedEmptySectionMarkup = new DOMParser()
      .parseFromString(emptySectionMarkup, 'text/html')
      .querySelector('.predictive-search-empty-section');

    if (!parsedEmptySectionMarkup) throw new Error('No empty section markup found');

    /** This needs to be awaited and not .then so the DOM is already morphed
     * when #closeResults is called and therefore the height is animated */
    const viewedProducts = RecentlyViewed.getProducts();

    if (viewedProducts.length > 0) {
      const recentlyViewedMarkup = await this.#getRecentlyViewedProductsMarkup();
      if (!recentlyViewedMarkup) return;

      const parsedRecentlyViewedMarkup = new DOMParser().parseFromString(recentlyViewedMarkup, 'text/html');
      const recentlyViewedProductsHtml = parsedRecentlyViewedMarkup.getElementById('predictive-search-products');
      if (!recentlyViewedProductsHtml) return;

      for (const child of recentlyViewedProductsHtml.children) {
        if (child instanceof HTMLElement) {
          child.setAttribute('ref', 'recentlyViewedWrapper');
        }
      }

      const collectionElement = parsedEmptySectionMarkup.querySelector('#predictive-search-products');
      if (!collectionElement) return;
      collectionElement.prepend(...recentlyViewedProductsHtml.children);
    }

    if (abortController.signal.aborted) return;

    morph(predictiveSearchResults, parsedEmptySectionMarkup);
    this.#resetScrollPositions();
  };
}

if (!customElements.get('predictive-search-component')) {
  customElements.define('predictive-search-component', PredictiveSearchComponent);
}
